import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import debounce from "lodash/debounce";
import { useSearchParams } from "react-router-dom";

import FormProductRecipe from "./FormProductRecipe";
import FormProduction from "./FormProduction";
import FormProductGroup, { createEmptyProductGroup } from "./FormProductGroup";
import DetailProductGroup from "./DetailProductGroup";
import { addProduction } from "../../actions/productAction";
import {
  addProductGroup,
  deleteProductGroup,
  fetchProductGroups,
  updateProductGroup,
} from "../../actions/productGroupAction";
import { fetchMaterials } from "../../actions/materialAction";
import { fetchUnits } from "../../actions/unitAction";
import { fetchProductCategories } from "../../actions/categoryAction";
import { fetchProductCategoryTypes } from "../../actions/categoryTypeAction";
import { addProductRecipe, fetchRecipeSources, updateProductRecipe } from "../../actions/productRecipeAction";
import { AlertConfirmModal } from "../../components/Modals/AlertConfirmModal";
import { sortHelper } from "../../utils/helpers/sortHelper";
import UserAvatar from "../../components/UserAvatar";
import { formatIDR } from "../../utils/helpers/formatIDR";
import { buildProductGroupPayload } from "./productGroupPayload";
import { hasAllPermissions } from "../../utils/helpers/permissions";

const isLandingDisplayEnabled = (item = {}) => {
  const flag = item?.is_display_landing ?? item?.display_landing ?? item?.show_on_landing ?? item?.is_featured;
  return flag === true || flag === 1 || flag === "1" || flag === "true";
};

const getProductCategoryName = (product = {}) => product?.category_label || product?.category || "-";
const getProductTypeName = (product = {}) => product?.type_label || product?.type || "-";

const getPriceRange = (group = {}) => {
  const minimum = Number(group?.minimum_price || 0);
  const maximum = Number(group?.maximum_price || minimum);
  return minimum === maximum ? formatIDR(minimum) : `${formatIDR(minimum)} – ${formatIDR(maximum)}`;
};

const createVariantState = () => ({
  id: "",
  name: "",
  type: "",
  category: "",
  weight: 1,
  unit: "pcs",
  unit_price: 0,
  unit_cogs: 0,
  stock: 0,
  min_stock: 0,
  max_stock: 0,
  image: "",
  description: "",
  qty_production: 1,
  materials: [],
  inventory_mode: "made_to_order",
  variant_label: "Reguler",
});

const ProductPage = ({
  dataProductGroups,
  productGroups,
  productGroupLoading,
  fetchProductGroups,
  addProductGroup,
  updateProductGroup,
  deleteProductGroup,
  fetchMaterials,
  materials,
  fetchUnits,
  units,
  fetchProductCategories,
  productCategories,
  fetchProductCategoryTypes,
  productTypes,
  addProductRecipe,
  fetchRecipeSources,
  updateProductRecipe,
  addProduction,
  userProfile,
}) => {
  const canProcessProduction = hasAllPermissions(userProfile, ["create-productions", "process-productions"]);
  const [perPage, setPerPage] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [variantState, setVariantState] = useState(createVariantState());
  const [groupFormState, setGroupFormState] = useState(createEmptyProductGroup());
  const [isGroupEdit, setIsGroupEdit] = useState(false);
  const [detailGroup, setDetailGroup] = useState(null);
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState(null);
  const [isProductionOpen, setIsProductionOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") || "";
  const [filterText, setFilterText] = useState(searchFromUrl);
  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();

  const closeVariantModal = () => {
    setVariantState(createVariantState());
    setIsProductionOpen(false);
  };

  const openGroupModal = (group = null) => {
    if (!group) {
      setIsGroupEdit(false);
      setGroupFormState(createEmptyProductGroup());
      return;
    }

    setIsGroupEdit(true);
    setGroupFormState({
      ...group,
      image: group?.image || group?.image_path || "",
      image_path: group?.image_path || group?.image || "",
      is_display_landing: Number(group?.is_display_landing || 0),
      variants: (group?.variants || []).map((variant) => ({
        ...variant,
        label: variant?.variant_label || "Reguler",
        recipes: variant?.materials || [],
      })),
    });
  };

  const closeGroupModal = () => {
    setIsGroupEdit(false);
    setGroupFormState(createEmptyProductGroup());
  };

  const handleSaveGroup = async () => {
    const payload = buildProductGroupPayload(groupFormState);
    const result = isGroupEdit ? await updateProductGroup(groupFormState.id, payload) : await addProductGroup(payload);

    if (result?.success) {
      await fetchProductGroups(perPage, currentPage, searchFromUrl);
      document.querySelector("#modal-product-group .btn-close")?.click();
      closeGroupModal();
    }
  };

  const handleDeleteGroup = async (id) => {
    const result = await deleteProductGroup(id);
    if (result?.success) {
      await fetchProductGroups(perPage, currentPage, searchFromUrl);
    }
    setPendingDeleteGroup(null);
  };

  const selectVariant = (group, variant) => ({
    ...variant,
    name: variant?.name || `${group?.name || "Produk"}${variant?.variant_label ? ` - ${variant.variant_label}` : ""}`,
    product_group_name: group?.name,
    category: variant?.category || group?.category,
    type: variant?.type || group?.type,
    image: variant?.image || group?.image,
    image_path: variant?.image || group?.image,
    image_url: variant?.image || group?.image,
    materials: variant?.materials || [],
    qty_production: 1,
  });

  const handleOpenRecipe = (group, variant) => {
    setDetailGroup(null);
    setVariantState(selectVariant(group, variant));
    setIsProductionOpen(false);
  };

  const handleOpenProduction = (group, variant) => {
    if (!canProcessProduction) return;
    setDetailGroup(null);
    setVariantState(selectVariant(group, variant));
    setIsProductionOpen(true);
  };

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setFilterText(value);
    setSearchParams(value ? { search: value } : {});
  };

  useEffect(() => {
    setFilterText(searchFromUrl);
  }, [searchFromUrl]);

  const debouncedFetch = useMemo(
    () =>
      debounce((page, limit, search) => {
        fetchProductGroups(limit, page, search);
      }, 500),
    [fetchProductGroups],
  );

  useEffect(() => {
    debouncedFetch(currentPage, perPage, searchFromUrl);
    return () => debouncedFetch.cancel();
  }, [currentPage, perPage, searchFromUrl, debouncedFetch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFromUrl]);

  useEffect(() => {
    fetchMaterials();
    fetchUnits(500);
    fetchProductCategories(500);
    fetchProductCategoryTypes(500);
  }, [fetchMaterials, fetchProductCategories, fetchProductCategoryTypes, fetchUnits]);

  const sortedGroups = getSortedData(productGroups);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Data Produk</h1>
              <div className="page-pretitle">katalog produk dan varian</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <button
                type="button"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#modal-product-group"
                onClick={() => openGroupModal()}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span className="d-none d-sm-inline">Tambah produk</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-table">
              <div className="card-header">
                <div className="row w-full align-items-center g-2">
                  <div className="col-md-8 col-12">
                    <h3 className="card-title mb-0">Katalog Produk</h3>
                    <p className="text-secondary m-0">
                      Satu baris untuk setiap produk; varian dan resep tersedia di detail.
                    </p>
                  </div>
                  <div className="col-md-4 col-12">
                    <div className="input-group input-group-flat">
                      <span className="input-group-text">
                        {productGroupLoading ? (
                          <span className="spinner-border spinner-border-sm text-primary" role="status" />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2">
                            <circle cx="10" cy="10" r="7" />
                            <path d="M21 21l-6-6" />
                          </svg>
                        )}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        onChange={handleSearchChange}
                        placeholder="Cari produk, varian, atau SKU..."
                        value={filterText}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-vcenter table-selectable">
                  <thead className="table-header">
                    <tr>
                      <th className="py-3 w-1">No.</th>
                      <sortHelper.SortableHeader
                        label="Produk"
                        sortKey="name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <th className="fw-bold text-center">Varian</th>
                      <th className="fw-bold d-table-cell">Harga</th>
                      <th className="fw-bold d-table-cell">Kesiapan</th>
                      <th className="fw-bold text-center  d-table-cell">Landing</th>
                      <th className="text-end d-table-cell" />
                    </tr>
                  </thead>
                  <tbody>
                    {productGroupLoading ? (
                      [...Array(productGroups?.length || 5)].map((_, index) => (
                        <tr key={`group-loading-${index}`}>
                          <td>{(currentPage - 1) * perPage + index + 1}.</td>
                          {[...Array(6)].map((__, cellIndex) => (
                            <td key={cellIndex}>
                              <div className="placeholder placeholder-lg w-75" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : sortedGroups.length ? (
                      sortedGroups.map((group, index) => {
                        const variants = group?.variants || [];
                        const recipeCount = variants.filter((variant) => (variant?.materials || []).length > 0).length;

                        return (
                          <tr key={group.id}>
                            <td>{(currentPage - 1) * perPage + index + 1}.</td>
                            <td data-label="Produk">
                              <div className="d-flex align-items-center py-1">
                                <UserAvatar
                                  fullName={group?.name}
                                  imageUrl={group?.image_path || group?.image}
                                  image_path={group?.image_path || group?.image}
                                  size="md"
                                  className="me-2"
                                  justPhotoView={false}
                                />
                                <div className="min-w-0">
                                  <div className="font-weight-medium text-truncate">{group?.name}</div>
                                  <div className="d-flex flex-wrap align-items-center gap-1 mt-1 fs-6">
                                    <span className="badge bg-secondary-lt">{getProductCategoryName(group)}</span>
                                    <span className="text-secondary">{getProductTypeName(group)}</span>
                                    <span className="text-secondary">• {group?.option_name || "Varian"}</span>
                                  </div>
                                  {/* <button
                                    type="button"
                                    className="btn btn-sm btn-primary d-inline-flex d-md-none mt-2"
                                    onClick={() => setDetailGroup(group)}>
                                    Lihat detail
                                  </button> */}
                                </div>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-primary-lt text-primary">{variants.length} varian</span>
                            </td>
                            <td className="fw-bold text-nowrap d-table-cell">{getPriceRange(group)}</td>
                            <td className="d-table-cell">
                              <div className="d-flex flex-column align-items-start gap-1">
                                <span
                                  className={`badge ${group?.can_sell ? "bg-success-lt text-success" : "bg-warning-lt text-warning"}`}>
                                  {group?.can_sell ? "Siap dijual" : "Perlu dilengkapi"}
                                </span>
                                <small className="text-secondary">
                                  {recipeCount}/{variants.length} resep tersedia
                                </small>
                              </div>
                            </td>
                            <td className="text-center d-table-cell">
                              <span
                                className={`badge ${isLandingDisplayEnabled(group) ? "bg-primary-lt" : "bg-secondary-lt"}`}>
                                {isLandingDisplayEnabled(group) ? "Tampil" : "Tidak"}
                              </span>
                            </td>
                            <td className="text-end d-table-cell">
                              <div className="btn-list flex-nowrap justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setDetailGroup(group)}>
                                  Detail
                                </button>
                                <div className="dropdown">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon bg-secondary-lt"
                                    data-bs-toggle="dropdown"
                                    aria-label={`Aksi lainnya untuk ${group?.name}`}>
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                      aria-hidden="true">
                                      <circle cx="5" cy="12" r="1.5" />
                                      <circle cx="12" cy="12" r="1.5" />
                                      <circle cx="19" cy="12" r="1.5" />
                                    </svg>
                                  </button>
                                  <div className="dropdown-menu dropdown-menu-end">
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-product-group"
                                      onClick={() => openGroupModal(group)}>
                                      Edit produk
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item text-danger"
                                      data-bs-toggle="modal"
                                      data-bs-target="#modal-delete-product-group"
                                      onClick={() => setPendingDeleteGroup(group)}>
                                      Hapus produk
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-secondary py-5">
                          {filterText ? "Produk tidak ditemukan." : "Belum ada produk. Tambahkan produk pertama Anda."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="card-footer d-flex align-items-center flex-wrap gap-2">
                <div className="dropdown">
                  <button className="btn dropdown-toggle" data-bs-toggle="dropdown">
                    {perPage} records
                  </button>
                  <div className="dropdown-menu">
                    {[10, 20, 50, 100].map((value) => (
                      <button key={value} className="dropdown-item" onClick={() => handlePerPageChange(value)}>
                        {value} records
                      </button>
                    ))}
                  </div>
                </div>
                <p className="m-0 text-secondary">
                  {dataProductGroups?.per_page || productGroups.length} dari{" "}
                  {dataProductGroups?.total || productGroups.length} data
                </p>
                <ul className="pagination m-0 ms-auto">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}>
                      prev
                    </button>
                  </li>
                  {dataProductGroups?.links?.slice(1, -1)?.map((item, index) => {
                    const pageNumber = item.url ? new URL(item.url).searchParams.get("page") : null;
                    return (
                      <li key={index} className={`page-item ${item?.active ? "active" : ""}`}>
                        <button
                          className="page-link"
                          disabled={!pageNumber}
                          onClick={() => pageNumber && setCurrentPage(Number(pageNumber))}>
                          {item.label}
                        </button>
                      </li>
                    );
                  })}
                  <li
                    className={`page-item ${currentPage >= Number(dataProductGroups?.last_page || 1) ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage((page) => page + 1)}>
                      next
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FormProductGroup
        isEdit={isGroupEdit}
        formState={groupFormState}
        setFormState={setGroupFormState}
        closeModal={closeGroupModal}
        onSubmit={handleSaveGroup}
        units={units}
        productCategories={productCategories}
        productTypes={productTypes}
        isLoading={productGroupLoading}
      />

      <FormProductRecipe
        closeModal={closeVariantModal}
        formState={variantState}
        setFormState={setVariantState}
        materials={materials}
        units={units}
        addProductRecipe={addProductRecipe}
        fetchRecipeSources={fetchRecipeSources}
        updateProductRecipe={updateProductRecipe}
      />

      <FormProduction
        isShowModal={isProductionOpen}
        isEdit
        closeModal={closeVariantModal}
        formState={variantState}
        setFormState={setVariantState}
        addProduction={addProduction}
        canProcess={canProcessProduction}
        units={units}
      />

      <DetailProductGroup
        group={detailGroup}
        onClose={() => setDetailGroup(null)}
        onOpenRecipe={handleOpenRecipe}
        onOpenProduction={handleOpenProduction}
        canProcessProduction={canProcessProduction}
      />

      <AlertConfirmModal
        type="error"
        title="Hapus produk ini?"
        message={`Produk ${pendingDeleteGroup?.name || "ini"} beserta seluruh variannya akan dinonaktifkan. Histori transaksi tetap tersimpan.`}
        confirmText="Hapus"
        cancelText="Batal"
        idModal="delete-product-group"
        idData={pendingDeleteGroup?.id}
        onConfirm={handleDeleteGroup}
        onCancel={() => setPendingDeleteGroup(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userProfile: state?.auth?.login?.profile,
  dataProductGroups: state?.productGroups?.datas,
  productGroups: state?.productGroups?.datas?.data || [],
  productGroupLoading: state?.productGroups?.loading,
  materials: state?.materials?.datas?.data || [],
  units: state?.units?.datas?.data || [],
  productCategories: state?.categories?.productDatas?.data || [],
  productTypes: state?.categoryTypes?.productDatas?.data || [],
});

export default connect(mapStateToProps, {
  fetchProductGroups,
  addProductGroup,
  updateProductGroup,
  deleteProductGroup,
  fetchMaterials,
  fetchUnits,
  fetchProductCategories,
  fetchProductCategoryTypes,
  addProductRecipe,
  fetchRecipeSources,
  updateProductRecipe,
  addProduction,
})(ProductPage);
